import * as Sentry from '@sentry/browser';
import { FTUE_LIST, getAssetList } from '../../Config/Assets';
import { GameItemConfig, ItemIDs } from '../../Config/Items';
import { TutorialLevelTriggers, TutorialStuckLevelTriggers } from '../../Config/Tutorial';
import GameLoader from '../../Core/Assets';
import Core from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../../DeltaTracking/AdParamBuilder';
import { ParamGroups } from '../../DeltaTracking/Tracker';
import { Actions } from '../../State/Actions/Actions';
import { IStateLevel, StuckReasons } from '../../State/reducers/level';
import { IStateScore } from '../../State/reducers/score';
import { IStateTutorial } from '../../State/reducers/tutorial';
import { Views } from '../../State/reducers/view';
import { IGameState } from '../../State/Store/store';
import { TileSprite } from '../../UI/Game/Board/TileSprite';
import { UIButton } from '../../UI/Shared/Buttons/UIButton';
import { Pausable } from '../../Utils/Pausable';
import { disableBoardAnimations } from '../../Utils/Platform';
import LevelView from '../../View/LevelView';
import Board from '../Board/Board';
import Boosters from '../Boosters/Boosters';
import { FocusManager } from '../FocusManager';
import { isNewPlayer } from '../Game';
import Helpers from '../Helpers';
import { Hud } from '../Hud/Hud';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import { Manager } from '../Manager';
import { Popup } from '../Popups';
import AdTracking, { VideoAdReasons } from '../Tracking/AdTracking';
import FTUETracking, { FTUE_TRACK_STAGES } from '../Tracking/FTUETracking';
import ResourceTracking from '../Tracking/ResourceTracking';
import { LoadStages, Tracking } from '../Tracking/Tracking';
import { LevelActions, LevelEmitter } from './LevelEmitter';
import WinSequence from './WinSequence';

const levelLogger: ILogger = getLogger('[LEVEL]: ');

export interface IBoosterConfig {
    id: string;
    loc?: PIXI.Point;
    delay?: number;
}

export default class Level extends Manager {
    public view: LevelView;
    private boardContainer: Board;
    private boosters: Boosters;
    private helpers: Helpers;
    private hud: Hud;
    private winSequence: WinSequence;
    private winCloseAction: () => void;

    constructor(hud: Hud) {
        super();
        this.hud = hud;

        this.winCloseAction = () => {
            this.retry();
        };

        // For some reason this static TILE_MATCH_TIME prop has to be called here or the game won't start
        // @ts-ignore
        const t = TileSprite.TILE_MATCH_TIME;
    }

    // TODO refactor IViewcontainer to get rid of first param
    public async init(data?: any, loadStart?: number) {
        Core.sdk.tracking.levelStart(Core.store.getState().StateLevel.levelIndex);
        await this.getLevelAssets(loadStart);

        this.hud.ui.hidePauseBtn();

        const board = Core.store.getState().StateBoard;
        this.view = new LevelView(
            board.boardSize,
            this.hud,
            Core.store.getState().StatePlayer.activeCollection
        );
        this.boardContainer = new Board(this.view.board);
        this.boosters = new Boosters(this.view, this.hud, this.boardContainer);
        this.helpers = new Helpers();
        this.winSequence = new WinSequence(this.view);

        this.initBoard().catch(err => {
            levelLogger.debug(['error init board', err]);
        });

        this.hud.ui.addGameUi();
        this.hud.ui.addPauseBtn();

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.levelComplete(state, pre);
        });

        this.addSelector((state: IGameState, pre: IGameState) => {
            if (state.StateCurrency.coin !== pre.StateCurrency.coin) {
                if (this.hud.ui.bottomUi) {
                    this.hud.ui.bottomUi.wallet.updatedCoinTxt(state.StateCurrency.coin);
                }
            }
        });

        this.addSelector(
            (state: IStateLevel, pre: IStateLevel) => {
                this.levelStuck(state, pre);
            },
            (state: IGameState) => state.StateLevel
        );

        this.addSelector(
            (state: IStateLevel, pre: IStateLevel) => {
                this.handleShuffle(state, pre);
            },
            (state: IGameState) => state.StateLevel
        );

        this.addSelector((currentState: IGameState, previousState: IGameState) => {
            const isPaused = currentState.StatePause.paused;
            const wasPaused = previousState.StatePause.paused;
            if (isPaused !== wasPaused && FocusManager.instance.hasFocus) {
                Pausable.setPaused(isPaused);
            }
        });

        this.addSelector(
            (state: IStateTutorial, pre: IStateTutorial) => {
                this.handleTutorial(state, pre);
            },
            (state: IGameState) => state.StateTutorial
        );

        this.addSelector((state: IGameState, pre: IGameState) => {
            if (NODE_ENV === 'editor' && state.StateView === Views.MAP) {
                const editorState = JSON.parse(window.name);
                editorState.score = state.StateScore.score;
                window.name = JSON.stringify(editorState);
                window.history.back();
            }
        });

        if (Core.DEBUG) {
            this.addSelector(
                (state: IStateScore, pre: IStateScore) => {
                    if (state.score !== pre.score) {
                        console.log('REPORT SCORE:: ' + state.score);
                    }
                },
                (state: IGameState) => state.StateScore
            );
        }
    }

    public async initBoard() {
        await this.boardContainer
            .initBoard()
            .then(() => {})
            .catch(err => {
                Sentry.captureException(err);
                Sentry.captureMessage('error with board init: ' + err);
                levelLogger.debug('board init error:::' + err);
            });

        const { levelIndex } = Core.store.getState().StateLevel;
        await this.displayLevelGoal(levelIndex);

        FTUETracking.track(FTUE_TRACK_STAGES.BOARD_SHOWN, Core.store.getState());
        Core.store.dispatch(Actions.boardReady(true));
        Core.store.dispatch(Actions.updateHearts(-1));

        HudEmitter.instance.emit(HudActions.LEVEL_INIT);
    }

    public update() {
        if (this.hud && this.hud.ui && this.hud.ui.topUi) {
            this.hud.ui.topUi.update();
        }
    }

    public cleanUp() {
        super.cleanUp();

        const state = Core.store.getState();

        Core.sdk.tracking.levelEnd(
            state.StateLevel.levelIndex,
            state.StateScore.score,
            state.StateLevel.complete ? 'win' : 'lose'
        );

        if (this.boosters) {
            this.boosters.cleanUp();
            this.boosters = null;
        }

        if (this.helpers) {
            this.helpers.cleanUp();
            this.helpers = null;
        }

        if (this.winSequence) {
            this.winSequence.cleanUp();
            this.winSequence = null;
        }

        if (this.boardContainer) {
            this.boardContainer.cleanUp();
            this.boardContainer = null;
        }

        if (this.view) {
            this.view.cleanUp();
            this.view.destroy();
            this.view = null;
        }

        HudEmitter.instance.emit(HudActions.HIDE_BOOSTER_PARACHUTE);
        LevelEmitter.instance.off(LevelActions.WIN_CLOSE, this.winCloseAction);
    }

    private async getLevelAssets(loadStart?: number): Promise<void> {
        const { StatePlayer } = Core.store.getState();
        if (isNewPlayer()) {
            await GameLoader.loadAssetList({ assetList: FTUE_LIST });
            if (loadStart) {
                const ftuetime = Date.now() - loadStart;
                Tracking.trackLoadTime(LoadStages.FTUE, ftuetime);
            }
        } else {
            const assets = getAssetList(Views.GAME, StatePlayer, isNewPlayer());
            await GameLoader.loadAssetList({ assetList: assets });
            if (loadStart) {
                const gametime = Date.now() - loadStart;
                Tracking.trackLoadTime(LoadStages.GAME, gametime);
            }
        }
    }

    private async displayLevelGoal(levelIndex: number) {
        if (
            !TutorialLevelTriggers.includes(levelIndex) ||
            TutorialStuckLevelTriggers.includes(levelIndex)
        ) {
            const { StateLevel: level, StatePlayer: player } = Core.store.getState();
            if (!disableBoardAnimations()) {
                Core.store.dispatch(Actions.popupShown());
            }
            await this.view.showLevelGoal(level.goal.type, player.activeCollection);

            if (!disableBoardAnimations()) {
                Core.store.dispatch(Actions.popupDismissed());
            }
        }
    }

    private async handleStuck() {
        const { StateLevel } = Core.store.getState();
        const { stuckReason, timed } = StateLevel;
        const boosterName = stuckReason === StuckReasons.MATCHES ? ItemIDs.SHUFFLE : ItemIDs.TIME;

        if (stuckReason === StuckReasons.MATCHES && timed) {
            setTimeout(() => {
                Core.store.dispatch(Actions.silentStoreBooster([boosterName]));
                this.boosters.handleBuyBooster(boosterName, 0);
            }, 1000);

            return;
        }
        this.view.showStuckPopup(
            stuckReason,
            stuckReason === StuckReasons.MATCHES && StateLevel.levelIndex === 15
        );
        const giveUpBtn = this.hud.ui.addGiveUpBtn();

        await this.view.failFlowAnimation();

        giveUpBtn.on(UIButton.events.Trigger, () => {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.MODAL);
        });
    }

    private retry() {
        HudEmitter.instance.emit(HudActions.LEVEL_RETRY);
        this.boardContainer.view.stopHints();

        const {
            StateBoosters: { currentBoosters, activeBoosters },
        } = Core.store.getState();
        const curBoosters = [...currentBoosters.slice(0), ...activeBoosters.slice(0)];
        curBoosters.forEach(id => {
            this.boosters.removeRunningBooster(id);
        });
        Core.store.dispatch(Actions.removeBoosters(curBoosters));

        if (Core.store.getState().StateCurrency.heart > 0) {
            Core.store.dispatch(Actions.updateHearts(-1));
            this.retrySuccess();
        } else {
            this.handleNeedHeart();
        }
        this.helpers.reset();
        HudEmitter.instance.emit(HudActions.LEVEL_INIT);
    }

    private retrySuccess() {
        this.boardContainer.retry();
        Core.store.dispatch(Actions.startLevel(Core.store.getState().StateLevel.levelId));
        Core.store.dispatch(Actions.boardReady(true));
        Core.store.dispatch(Actions.blockBoard(false));
    }

    private handleNeedHeart() {
        this.hud.popups.showInsuffiecientFunds([
            'heart',
            async (action: string) => {
                if (action === 'buy') {
                    if (Core.delta) {
                        const savedDeltaData = Core.delta.trackCurPropGetters.get(
                            ParamGroups.SAVED
                        )();
                        Core.delta.setSavedParams({
                            livesBoughtWithCoins: savedDeltaData.livesBoughtWithCoins + 1,
                        });
                    }
                    Core.store.dispatch(Actions.updateCoins(-GameItemConfig.heart.price));
                    ResourceTracking.heartBought(); // delta resource event sent
                    this.retrySuccess();
                }
                if (action === 'watch') {
                    const adSuccess = () => {
                        AdTracking.trackAdWatched(VideoAdReasons.HEART);
                        Core.store.dispatch(Actions.updateHearts(1));
                        this.retrySuccess();
                    };
                    const adError = () => {
                        HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.AD_ERROR, 2);
                    };
                    if (Core.delta) {
                        Core.delta.setAdParams(
                            AdRewardTypes.COINS,
                            AdContexts.LIFE_NEEDED,
                            AdGameAreas.LEVEL,
                            Core.store.getState().StateLevel.levelIndex
                        );
                    }

                    HudEmitter.instance.emit(
                        HudActions.WATCH_REWARDED_AD,
                        'coins',
                        adSuccess,
                        adError
                    );
                }
            },
            () => {
                HudEmitter.instance.emit(HudActions.QUIT_GAME);
            },
        ]);
    }

    private levelComplete(state: IGameState, previous: IGameState) {
        if (state.StateLevel.complete && !previous.StateLevel.complete) {
            HudEmitter.instance.emit(HudActions.HIDE_BOOSTER_PARACHUTE);
            this.helpers.destroyHelperTimer();
        }
    }

    private levelStuck(state: IStateLevel, previous: IStateLevel) {
        if (state.stuck && !previous.stuck) {
            if (!state.complete) {
                this.helpers.destroyHelperTimer();
                HudEmitter.instance.emit(HudActions.HIDE_BOOSTER_PARACHUTE);
                this.handleStuck();
            }
        } else if (!state.stuck && previous.stuck) {
            HudEmitter.instance.emit(HudActions.HIDE_BOOSTER_PARACHUTE);
            this.view.closeStuckPopup();
            this.hud.ui.hideGiveUpBtn();
        }
    }

    private handleShuffle(state: IStateLevel, previous: IStateLevel) {
        if (state.shuffling && !previous.shuffling) {
            this.view.showShuffle();
        }
    }

    private handleTutorial(state: IStateTutorial, previous: IStateTutorial) {
        if (this.view) {
            if (state.index !== -1 && previous.index === -1) {
                this.view.screenOverlay(true, 300);
            }

            if (state.index === -1 && previous.index !== -1) {
                this.view.screenOverlay(false, 300);
            }
        }
    }
}
